import {createSelector} from 'reselect';
import DownloadState from './DownloadState';
import {
    Feature,
    FeaturesByType,
    FeatureTypeIdWithValidOperations, Operation
} from '../../../integration/representation/feature';
import {FeatureTypeSummary} from '../../../integration/representation/feature-type';


const stateDatacatalog = (state: DownloadState) => state.datacatalog;
const stateFeatures = (state: DownloadState) => state.features;
const stateFeatureTypes = (state: DownloadState) => state.featureTypes;
const stateDestination = (state: DownloadState) => state.destination;
const stateUseProjectRef = (state: DownloadState) => state.useProjectRef;
const stateUseContractProjectRef = (state: DownloadState) => state.useContractProjectRef;
const stateContractProjectRef = (state: DownloadState) => state.contractProjectRef;
const stateCustomProjectRef = (state: DownloadState) => state.customProjectRef;
const stateSosiForNvdb123 = (state: DownloadState) => state.sosiForNvdb123;
const stateOperations = (state: DownloadState) => state.operations;
const stateSrid = (state: DownloadState) => state.srid;
const stateSelectedFeatureTypeIds = (state: DownloadState) => state.selectedFeatureTypeIds;
const stateLoading = (state: DownloadState) => state.loading;


export const destination = createSelector(
    [stateDestination],
    (destination) => {
        return destination;
    }
);

export const useProjectRef = createSelector(
    [stateUseProjectRef],
    (useProjectRef) => {
        return useProjectRef;
    }
);

export const useContractProjectRef = createSelector(
    [stateUseContractProjectRef],
    (useContractProjectRef) => {
        return useContractProjectRef;
    }
);

export const contractProjectRef = createSelector(
    [stateContractProjectRef],
    (contractProjectRef) => {
        return contractProjectRef;
    }
);

export const customProjectRef = createSelector(
    [stateCustomProjectRef],
    (customProjectRef) => {
        return customProjectRef;
    }
);

export const sosiForNvdb123 = createSelector(
    [stateSosiForNvdb123],
    (sosiForNvdb123) => {
        return sosiForNvdb123;
    }
);

export const operations = createSelector(
    [stateOperations],
    (selectedOperations) => {
        return selectedOperations;
    }
);

export const selectedFeatureTypeIds = createSelector(
    [stateSelectedFeatureTypeIds],
    (selectedFeatureTypeIds) => {
        return selectedFeatureTypeIds;
    }
);

export const selectedFeatureTypeIdsWithValidAndSelectedOperations = createSelector(
    [stateFeatures, stateSelectedFeatureTypeIds, stateOperations],
    (features, selectedIds, selectedOperations): FeatureTypeIdWithValidOperations => {
        const initValue: FeatureTypeIdWithValidOperations = {};
        return features
            .filter(feature => selectedIds.indexOf(feature.typeId) !== -1 && selectedOperations.indexOf(feature.operation) !== -1)
            .reduce((prev, curr) => ({...prev, [curr.typeId]: prev[curr.typeId] ? prev[curr.typeId].add(curr.operation) : new Set<Operation>().add(curr.operation)}), initValue);
    }
);

export const srid = createSelector(
    [stateSrid],
    (srid) => {
        return srid;
    }
);

export const loading = createSelector(
    [stateLoading],
    (laoding) => {
        return laoding;
    }
);

const nvdbFeatures = createSelector(
    [stateFeatures],
    (features) => {
        return features.filter(feature => feature.destination === 'NVDB');
    }
);

const fkbFeatures = createSelector(
    [stateFeatures],
    (features) => {
        return features.filter(feature => feature.destination === 'FKB');
    }
);

const sortedNvdbFeatures = createSelector(
    [nvdbFeatures],
    (features) => {
        type CompareType = (a: Feature, b: Feature) => number;

        const compareTypes = (intlCollator: Intl.Collator): CompareType => {
            return function compareWithIntlCollator (a: Feature, b: Feature): number {
                return intlCollator.compare(a.name, b.name);
            };
        };

        const sortFeatures = (features: Feature[], intlCollator: Intl.Collator): Feature[] => {
            features.sort(compareTypes(intlCollator));
            return features;
        };

        const intlCollator = Intl.Collator('nb', { numeric: true });

        const sorted = sortFeatures(features, intlCollator);
        return sorted;
    }
);

const sortedFkbFeatures = createSelector(
    [fkbFeatures],
    (features) => {
        type CompareType = (a: Feature, b: Feature) => number;

        const compareTypes = (intlCollator: Intl.Collator): CompareType => {
            return function compareWithIntlCollator (a: Feature, b: Feature): number {
                return intlCollator.compare(a.name, b.name);
            };
        };

        const sortFeatures = (features: Feature[], intlCollator: Intl.Collator): Feature[] => {
            features.sort(compareTypes(intlCollator));
            return features;
        };

        const intlCollator = Intl.Collator('nb', { numeric: true });

        const sorted = sortFeatures(features, intlCollator);
        return sorted;
    }
);

export const sortedFeatureTypes = createSelector(
    [stateFeatureTypes],
    (featureTypes) => {

        type CompareType = (a: FeatureTypeSummary, b: FeatureTypeSummary) => number;

        const compareTypes = (intlCollator: Intl.Collator): CompareType => {
            return function compareWithIntlCollator (a: FeatureTypeSummary, b: FeatureTypeSummary): number {
                return intlCollator.compare(a.name, b.name);
            };
        };

        const sortTypes = (types: FeatureTypeSummary[], intlCollator: Intl.Collator): FeatureTypeSummary[] => {
            types.sort(compareTypes(intlCollator));
            return types;
        };

        const intlCollator = Intl.Collator('nb', { numeric: true });

        const sortedTypes = sortTypes(featureTypes, intlCollator);
        return sortedTypes;
    }
);

const nvdbFeaturesByType = createSelector(
    [stateDatacatalog, sortedNvdbFeatures],
    (datacatalog, features) => {

        const featuresByType: FeaturesByType[] = [];

        // Finner alle distinkte vegobjekttyper
        const distinctTypes = new Set(features.map(object => object.typeId));

        // Fyller lista featuresByType med alle vegobjekttyper
        for (const typeId of distinctTypes) {
            // Fikk en undefined error på getFeatureType(typeId), så legger inn en sjekk. Bør jo ikke skje da?
            if (datacatalog.getFeatureType(typeId)) {
                featuresByType.push({
                    type: {
                        typeId,
                        name: datacatalog.getFeatureType(typeId).name
                    },
                    features: []
                });
            }
        }

        // Sorterer vegobjekttyper alfabetisk på navn
        type CompareType = (a: FeaturesByType, b: FeaturesByType) => number;

        const compareTypes = (intlCollator: Intl.Collator): CompareType => {
            return function compareWithIntlCollator (a: FeaturesByType, b: FeaturesByType): number {
                return intlCollator.compare(a.type.name, b.type.name);
            };
        };

        const sortTypes = (types: FeaturesByType[], intlCollator: Intl.Collator): FeaturesByType[] => {
            types.sort(compareTypes(intlCollator));
            return types;
        };

        const intlCollator = Intl.Collator('nb', { numeric: true });

        sortTypes(featuresByType, intlCollator);

        // Legger vegobjekter på tilhørende vegobjekttype
        for (const feature of features) {
            featuresByType
                .find(featureByType => featureByType.type.typeId === feature.typeId)?.features
                .push(feature);
        }

        return featuresByType;
    }
);

const fkbFeaturesByType = createSelector(
    [stateDatacatalog, sortedFkbFeatures],
    (datacatalog, features) => {

        const featuresByType: FeaturesByType[] = [];


        // Finner alle distinkte vegobjekttyper
        const distinctTypes = new Set(features.map(object => object.typeId));

        for (const typeId of distinctTypes) {
            featuresByType.push({
                type: {
                    typeId,
                    name: datacatalog.getFkbFeatureType(typeId)?.name ?? "Ukjent FKB-type"
                },
                features: []
            });
        }

        // Sorterer vegobjekttyper alfabetisk på navn
        type CompareType = (a: FeaturesByType, b: FeaturesByType) => number;

        const compareTypes = (intlCollator: Intl.Collator): CompareType => {
            return function compareWithIntlCollator (a: FeaturesByType, b: FeaturesByType): number {
                return intlCollator.compare(a.type.name, b.type.name);
            };
        };

        const sortTypes = (types: FeaturesByType[], intlCollator: Intl.Collator): FeaturesByType[] => {
            types.sort(compareTypes(intlCollator));
            return types;
        };

        const intlCollator = Intl.Collator('nb', { numeric: true });

        sortTypes(featuresByType, intlCollator);

        // Legger vegobjekter på tilhørende vegobjekttype
        for (const feature of features) {
            featuresByType
                .find(featureByType => featureByType.type.typeId === feature.typeId).features
                .push(feature);
        }

        return featuresByType;
    }
);

export const featuresByTypeAndDestination = createSelector(
    [stateDestination, nvdbFeaturesByType, fkbFeaturesByType],
    (destination, nvdbFeatures, fkbFeatures) => {

        if (destination === 'NVDB') {
            return nvdbFeatures;
        } else if (destination === 'FKB') {
            return fkbFeatures;
        } else {
            throw Error('Unsupported destination: ' + destination);
        }
    }
);

export const validOperations = createSelector(
    [stateFeatures],
    (features) => {
        return new Set(features.map(feature => feature.operation));
    }
);

export const enableDownloadButton = createSelector(
    [stateFeatures, stateSelectedFeatureTypeIds, stateOperations],
    (features, selectedTypeIds, operations) => {

        // Returnerer true hvis vi har minst ett objekt som kan lastes ned
        for (const feature of features) {
            if (operations.some(op => feature.operation === op) && selectedTypeIds.indexOf(feature.typeId) !== -1) {
                return true;
            }
        }
        return false;
    }
);
